import * as cheerio from 'cheerio';
// eslint-disable-next-line import/no-cycle
import {
  getSmartListHtmlELementFromState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { Maybe } from '@/types/generated-types/graphql';

// eslint-disable-next-line import/no-cycle
import { ListSelectionTypeEnum, SmartProductListLayoutEnum } from '@/types';

// eslint-disable-next-line import/no-cycle
import { TEMPLATE_WIDGET_IDENTIFIER_PREFIX } from '@/components/template-builder/utils/constants';

/**
 * Get specific smart list property from original HTML code
 * @param selector
 * @param name
 * @param widgetId
 */
export const getSmartListProperty = (selector: string, name: string, widgetId: string): string => {
  let returnValue = '';
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    const regExp = new RegExp(`.*s-${name}="([^"]*)"`, 'is');
    const matches = element.match(regExp);
    returnValue = (matches && matches[1]) ?? '';
  }

  return returnValue;
};

/**
 * Check if the smart list targeted by the given resolved selector uses a manual product selection.
 * With that selection type the engine returns exactly the selected products, so properties driving
 * the number of returned products (like "nb") are ignored.
 * @param selector resolved selector, e.g. "body #spm_body #spm_widget_xxx .spm_product_list_element_container"
 */
export const isSmartListManualSelection = (selector: string): boolean => {
  const matches = selector.match(new RegExp(`#(${TEMPLATE_WIDGET_IDENTIFIER_PREFIX}[0-9a-zA-Z_-]+)`));

  if (!matches) {
    return false;
  }

  return getSmartListProperty(selector, 'engine', matches[1]) === ListSelectionTypeEnum.MANUAL_SELECTION;
};

export const getSmartListHtmlCode = (selector: string, name: string, widgetId: string) => {
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    return element;
  }

  return '';
};

export const getSmartListHtmlCodeEmailTemplate = (selector: string, name: string, widgetId: string) => {
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    // If content is wrapped like <tbody><tr><th>...</th></tr></tbody>, strip the wrapper and return inner content
    const trimmed = element.trim();
    const match = trimmed.match(/^<tbody(?:\s[^>]*)?>\s*<tr(?:\s[^>]*)?>\s*<th(?:\s[^>]*)?>([\s\S]*)<\/th>\s*<\/tr>\s*<\/tbody>\s*$/i);
    if (match) {
      return match[1];
    }
    return element;
  }

  return '';
};

export const getTruncateLimit = (variable: string, name: string, widgetId: string) => {
  let returnValue = '';
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    const regExp = new RegExp(`\\{var="?${variable}"?[^}]*truncate="?([0-9]+)"?[^}]*}`, 'is');
    const matches = element.match(regExp);
    returnValue = (matches && matches[1]) ?? '';
  }

  return returnValue;
};

export const getDisplayedElements = (selector: string, name: string, widgetId: string): string[] => {
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  // Elements are hidden by adding "spm_hide_on_builder" class
  const allElements = [
    'spm_product_list_element_button',
    'spm_product_list_element_description',
    'spm_product_list_element_price',
    'spm_product_list_element_price_strike',
    'spm_product_list_element_image',
    'spm_product_list_element_title',
    'spm_product_list_title',
  ];

  const displayedElements: string[] = [];

  if (element) {
    allElements.forEach((displayableElement) => {
      // We check if class exists or not
      const regExp = new RegExp(`"([^"]*\\b${displayableElement}\\b(?:\\s[^"]+)*)"`, 'is');
      const matches = element?.match(regExp);

      if (matches) {
        const [, match] = matches;
        const classList = match.split(' ');

        if (!classList.includes('spm_hide_on_builder')) {
          // If we need to display the item, we need to remove the class
          displayedElements.push(displayableElement);
        }
      }
    });
  }

  return displayedElements;
};

export const getListLayout = (selector: string, name: string, widgetId: string): string => {
  const element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  let returnValue = '';

  if (element) {
    const regExp = new RegExp('"([^"]*spm_product_list(?:\\s[^"]+)*)"', 'is');
    const matches = element?.match(regExp);

    if (matches) {
      const [, match] = matches;
      const classList = match.split(' ');
      const layout = classList.filter((className) => className.startsWith(name, 0)).map((className) => className.replace(name, ''));
      if (layout.length) {
        [returnValue] = layout;
      } else {
        returnValue = SmartProductListLayoutEnum.CUSTOM;
      }
    } else {
      returnValue = SmartProductListLayoutEnum.CUSTOM;
    }
  }

  return returnValue;
};

/**
 * Extract relative selector by stripping the widget ID and everything before it.
 * Config selectors are absolute (e.g., "body #spm_body #spm_widget_xxx .spm_product_list_title")
 * but state stores widget.innerHTML, so we need a selector relative to the widget content.
 */
const extractRelativeSelector = (selector: string, widgetId: string): string => {
  const idSelector = `#${widgetId}`;
  const idx = selector.indexOf(idSelector);
  if (idx >= 0) {
    let rest = selector.slice(idx + idSelector.length).trimStart();
    // Remove leading child combinator if present
    if (rest.startsWith('>')) {
      rest = rest.slice(1).trimStart();
    }
    return rest;
  }
  return selector;
};

export const getSmartListTitle = (selector: string, name: string, widgetId: string) => {
  const stateHtml: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml) return undefined;

  const $ = cheerio.load(stateHtml, null, false);
  const relativeSelector = extractRelativeSelector(selector, widgetId);
  const target = $(relativeSelector);
  if (!target.length) return undefined;

  // Strip translation wrappers — return inner content of <spmtranslation> if present
  const translation = target.find('spmtranslation').first();
  return translation.length ? translation.html() : target.html();
};

export const getSmartListContent = (selector: string, name: string, widgetId: string) => {
  const stateHtml: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml) return '';

  const $ = cheerio.load(stateHtml, null, false);
  const relativeSelector = extractRelativeSelector(selector, widgetId);
  const target = $(relativeSelector);
  if (!target.length) return '';

  // Strip translation wrappers — return inner content of <spmtranslation> if present
  const translation = target.find('spmtranslation').first();
  return translation.length ? translation.html() ?? '' : target.html() ?? '';
};
